import { AI_IMAGE_USER_ERROR_CODES, AiImageUserInputError } from '../../errors';

export type TargetTier = '1k' | '2k' | '4k' | '8k' | '16k';

const UPSCALE_FACTOR_MIN = 1;
const UPSCALE_FACTOR_MAX = 10;

export type SeedVrTargetResolution = '1080p' | '1440p' | '2160p';

export const TARGET_RESOLUTION_BY_TIER: Record<
  Exclude<TargetTier, '8k' | '16k'>,
  SeedVrTargetResolution
> = {
  '1k': '1080p',
  '2k': '1440p',
  '4k': '2160p',
};

export const TARGET_SHORT_EDGE_BY_RESOLUTION: Record<
  SeedVrTargetResolution,
  number
> = {
  '1080p': 1080,
  '1440p': 1440,
  '2160p': 2160,
};

export const TARGET_SHORT_EDGE_BY_TIER: Record<TargetTier, number> = {
  '1k': TARGET_SHORT_EDGE_BY_RESOLUTION['1080p'],
  '2k': TARGET_SHORT_EDGE_BY_RESOLUTION['1440p'],
  '4k': TARGET_SHORT_EDGE_BY_RESOLUTION['2160p'],
  '8k': 4320,
  '16k': 8640,
};

function getSourceShortEdge(width: number, height: number) {
  const shortEdge = Math.min(width, height);
  if (!Number.isFinite(shortEdge) || shortEdge <= 0) {
    throw new Error('Invalid input image dimensions');
  }
  return shortEdge;
}

export function isAtOrAboveTargetResolutionByDimensions(params: {
  width: number;
  height: number;
  targetResolution: SeedVrTargetResolution;
}) {
  const sourceShortEdge = getSourceShortEdge(params.width, params.height);
  const targetShortEdge =
    TARGET_SHORT_EDGE_BY_RESOLUTION[params.targetResolution];
  return sourceShortEdge >= targetShortEdge;
}

export function isAtOrAboveTargetTierByDimensions(params: {
  width: number;
  height: number;
  targetTier: TargetTier;
}) {
  const sourceShortEdge = getSourceShortEdge(params.width, params.height);
  const targetShortEdge = TARGET_SHORT_EDGE_BY_TIER[params.targetTier];
  return sourceShortEdge >= targetShortEdge;
}

export function resolveRequiredFactor(params: {
  width: number;
  height: number;
  targetTier: TargetTier;
}) {
  const sourceShortEdge = getSourceShortEdge(params.width, params.height);
  const targetShortEdge = TARGET_SHORT_EDGE_BY_TIER[params.targetTier];
  return targetShortEdge / sourceShortEdge;
}

export function isSourceTooSmallForTier(params: {
  width: number;
  height: number;
  targetTier: TargetTier;
}) {
  return resolveRequiredFactor(params) > UPSCALE_FACTOR_MAX;
}

export function resolveFactorForProvider(params: {
  width: number;
  height: number;
  targetTier: TargetTier;
}) {
  const raw = resolveRequiredFactor(params);
  const roundedUpToTenth = Math.ceil(raw * 10) / 10;
  return Math.min(
    UPSCALE_FACTOR_MAX,
    Math.max(UPSCALE_FACTOR_MIN, roundedUpToTenth)
  );
}

export type SeedVrModeInput =
  | {
      upscale_mode: 'target';
      target_resolution: SeedVrTargetResolution;
    }
  | {
      upscale_mode: 'factor';
      upscale_factor: number;
    };

/** SeedVR rejects sub-256px inputs when the requested output exceeds 1080p. */
export function assertSeedVrSourceSize(params: {
  width: number;
  height: number;
  input: Record<string, unknown>;
}) {
  const { width, height, input } = params;
  // These are the provider's documented defaults for omitted mode/factor.
  const factor = input.upscale_factor ?? 2;
  const exceeds1080p =
    input.upscale_mode === 'target'
      ? input.target_resolution === '1440p' ||
        input.target_resolution === '2160p'
      : typeof factor === 'number' &&
        width * height * factor * factor > 1920 * 1080;
  if (Math.min(width, height) < 256 && exceeds1080p) {
    throw new AiImageUserInputError(
      'Choose a lower resolution or upload an image at least 256 pixels wide and tall.',
      AI_IMAGE_USER_ERROR_CODES.UPSCALE_SOURCE_TOO_SMALL_FOR_TARGET
    );
  }
}

export function resolveSeedVrModeInput(params: {
  width: number;
  height: number;
  targetTier: TargetTier;
}): SeedVrModeInput {
  if (params.targetTier === '8k' || params.targetTier === '16k') {
    return {
      upscale_mode: 'factor',
      upscale_factor: resolveFactorForProvider(params),
    };
  }

  return {
    upscale_mode: 'target',
    target_resolution: TARGET_RESOLUTION_BY_TIER[params.targetTier],
  };
}
